import { Attachment, Edit } from "@mui/icons-material";
import { Avatar, Badge, Box, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { LeadService } from "../../services";
import { CardLayout, EmptyState, IconButton } from "../v3";
import { AssigneeRow } from "./AssigneeRow.component";
import { DocumentsModal } from "./DocumentsModal.component";

export const LeadDetails = ({
  leadDetails,
  leadTaskAssignees,
  onEdit,
  onActivityChange = () => {},
  canUploadDocument,
  canDeleteDocument,
}: any) => {
  const [openDocsModal, setOpenDocsModal] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    documentId: null,
  });
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);

  useEffect(() => {
    if (leadDetails?.id) fetchDocuments();
  }, [leadDetails?.id]);

  const fetchDocuments = async () => {
    try {
      const docs = await LeadService.getLeadDocuments(leadDetails.id);
      setDocuments(docs);
    } catch (error) {
      console.error("fetchDocuments error:", error);
    }
  };

  const handleUpload = async (file: File) => {
    setIsUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append("document", file);
      await LeadService.uploadLeadDocument(leadDetails.id, formData);
      toast.success("Document uploaded successfully");
      fetchDocuments();
      onActivityChange();
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to upload document";
      toast.error(typeof msg === "string" ? msg : Array.isArray(msg) ? msg.join(", ") : "An error occurred");
      console.error("handleUpload error:", error);
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const openDeleteModal = (documentId: number) => {
    setDeleteModal({ isOpen: true, documentId });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, documentId: null });
  };

  const handleDeleteDocument = async () => {
    if (!deleteModal.documentId) return;

    setIsDeletingDocument(true);
    try {
      await LeadService.deleteLeadDocument(leadDetails.id, deleteModal.documentId);
      toast.success("Document deleted successfully");
      fetchDocuments();
      onActivityChange();
      closeDeleteModal();
    } catch (error) {
      toast.error("Failed to delete document");
      console.error("handleDeleteDocument error:", error);
    } finally {
      setIsDeletingDocument(false);
    }
  };

  const siteAddressLabel = [leadDetails?.siteAddress?.locality, leadDetails?.siteAddress?.city, leadDetails?.siteAddress?.state]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <CardLayout sx={{ padding: "14px" }}>
        <Box
          sx={{
            backgroundColor: "#F6F6F6",
            marginTop: "12px",
            padding: "14px",
            borderRadius: "10px",
          }}
        >
          {/* Client details*/}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: "14px",
              borderBottom: "1px solid #E5E5E5",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* Profile Photo */}
              <Box
                sx={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "50%",
                  overflow: "hidden",
                  marginRight: "16px",
                }}
              >
                <Avatar
                  src={leadDetails?.imageUrl}
                  alt="Profile"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    backgroundColor: "#F2ECF9",
                  }}
                />
              </Box>

              {/* Details */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {leadDetails.contactName}
                </Typography>
                <Typography variant="caption" display="block">
                  {leadDetails.contactPhone}
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Badge badgeContent={documents.length} color="primary">
                <IconButton
                  buttonText={"Documents"}
                  icon={<Attachment />}
                  tooltip="View All Documents"
                  styles={{
                    color: "#ffffff",
                    backgroundColor: "#c6c5c5",
                    fontSize: "12px",
                    padding: "3px 8px",
                  }}
                  hoverStyles={{ backgroundColor: "#ecddfdff" }}
                  onClick={() => setOpenDocsModal(true)}
                />
              </Badge>
              {onEdit && (
                <IconButton
                  buttonText={"Edit"}
                  icon={<Edit sx={{ fontSize: 16 }} />}
                  tooltip="Edit Lead"
                  styles={{
                    color: "#ffffff",
                    backgroundColor: "#c6c5c5",
                    fontSize: "12px",
                    padding: "3px 8px",
                  }}
                  hoverStyles={{ backgroundColor: "#ecddfdff" }}
                  onClick={onEdit}
                />
              )}
            </Stack>
          </Box>

          <Box sx={{ marginTop: "14px" }}>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Priority
                </Typography>
                <Typography variant="body2">{leadDetails?.priority?.name || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Source
                </Typography>
                <Typography variant="body2">{leadDetails?.source || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Expected Budget
                </Typography>
                <Typography variant="body2">{leadDetails?.expectedBudget ?? "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Expected Start Date
                </Typography>
                <Typography variant="body2">{leadDetails?.expectedStartDate || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Site Location
                </Typography>
                <Typography variant="body2">{siteAddressLabel || "—"}</Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ marginTop: "14px" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: "10px" }}>
              Assignees
            </Typography>

            {leadTaskAssignees?.length > 0 ? (
              <AssigneeRow assignees={leadTaskAssignees} maxVisible={3} />
            ) : (
              <EmptyState title="No assignees added yet" subtitle="Get started by assigning a task to someone!" />
            )}
          </Box>
        </Box>
      </CardLayout>
      <DocumentsModal
        open={openDocsModal}
        onClose={() => setOpenDocsModal(false)}
        documents={documents}
        canUpload={canUploadDocument}
        canDelete={canDeleteDocument}
        onUpload={handleUpload}
        onDelete={openDeleteModal}
        isUploading={isUploadingDocument}
        deleteModal={deleteModal}
        onCloseDeleteModal={closeDeleteModal}
        onConfirmDelete={handleDeleteDocument}
        isDeletingDocument={isDeletingDocument}
      />
    </>
  );
};
